/**
 * Import d'un export **Password Boss** JSON (« Parametres > Exporter » de
 * Password Boss, gestionnaire des MSP : ses utilisateurs doivent pouvoir partir
 * avec TOUT leur coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le fichier porte `folders` (`{ id, name }`) et `items`.
 * Chaque element a un `type` (`WebLogin`, `CreditCard`...), un `name`, une
 * adresse `login_url`, l'`id` de son dossier en `folder`, et ses valeurs dans
 * `identifiers` (`username`, `email`, `password`, `totp`, `notes`,
 * `custom_fields`, et pour une carte `cardNumber`, `nameOnCard`,
 * `security_code`, `expires`). Toute valeur non reprise devient un champ
 * personnalise, masque si son libelle designe un secret (aucune perte).
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, joinNotes, normalizeTotpSecret, type ImportedEntry } from './csv-import.js';
import { normalizeTypedFields } from './typed-fields.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

function asArray(value: Json): readonly Json[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: Json): string {
  return typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

/** Valeur brute d'un secret : jamais rognee, une espace peut en faire partie. */
function rawSecret(value: Json): string {
  return typeof value === 'string' ? value : asString(value);
}

function objects(value: Json): Record<string, Json>[] {
  return asArray(value)
    .map(asObject)
    .filter((item): item is Record<string, Json> => item !== null);
}

/** Cles d'adresse reconnues dans `identifiers` quand l'element n'a pas de `login_url`. */
const URL_KEYS = new Set(['url', 'uri', 'website', 'web_site', 'login_url', 'address', 'host']);

/** Drapeaux internes de Password Boss, sans valeur pour l'utilisateur. */
const IGNORED_IDENTIFIERS = new Set(['notes', 'ignoreItemInSecurityScore']);

/**
 * Les notes Password Boss arrivent parfois avec des sauts de ligne echappes
 * (`\r\n` litteral) : ils redeviennent de vrais sauts de ligne. Le texte garde
 * sinon sa mise en forme (retraits compris).
 */
function passwordBossNotes(value: Json): string {
  const text = typeof value === 'string' ? value : asString(value);
  return text.replace(/\\r\\n|\\n/gu, '\n');
}

/**
 * Expiration d'une carte : date ISO (`2027-03-31T00:00:00`) ou `MM/AA(AA)`
 * -> `MM/AAAA` ; toute autre forme reste un champ, jamais perdue. Lue sans
 * `Date` : un fuseau horaire ne doit pas faire reculer l'expiration d'un mois.
 */
function cardExpiry(raw: string): string | null {
  const iso = /^(\d{4})-(\d{1,2})(?:[-T ]|$)/u.exec(raw);
  if (iso !== null) return `${iso[2]!.padStart(2, '0')}/${iso[1]}`;
  const short = /^(\d{1,2})\s*\/\s*(\d{2}|\d{4})$/u.exec(raw);
  if (short !== null) return `${short[1]!.padStart(2, '0')}/${short[2]!.length === 2 ? `20${short[2]}` : short[2]}`;
  return null;
}

/** Champs personnalises Password Boss : chaque `{ name, value }` devient un champ, masque si secret. */
function customFieldList(value: Json): EntryCustomField[] {
  return objects(value)
    .map((field) => csvColumnField(asString(field['name']) || 'Champ', rawSecret(field['value'])))
    .filter((field) => field.value.length > 0);
}

/** Nom de repli d'une entree : l'hote de son adresse, sans `www.`. */
function hostOf(url: string): string {
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//iu.test(url) ? url : `https://${url}`).hostname.replace(/^www\./iu, '');
  } catch {
    return url;
  }
}

function withFields(customFields: readonly EntryCustomField[]): { customFields?: readonly EntryCustomField[] } {
  return customFields.length > 0 ? { customFields } : {};
}

function parsePasswordBossItem(item: Record<string, Json>, folders: ReadonlyMap<string, string>): ImportedEntry {
  const identifiers = asObject(item['identifiers']) ?? {};
  const isCard = asString(item['type']).toLowerCase() === 'creditcard';
  const typed: Record<string, string> = {};
  const customFields: EntryCustomField[] = [];
  let username = '';
  let password = '';
  let url = asString(item['login_url']);
  let totp = { secret: '', extraNote: '' };

  for (const [key, value] of Object.entries(identifiers)) {
    if (IGNORED_IDENTIFIERS.has(key)) continue;
    if (key === 'custom_fields') {
      customFields.push(...customFieldList(value));
      continue;
    }
    const text = Array.isArray(value) ? value.map(asString).filter(Boolean).join(', ') : asObject(value) !== null ? JSON.stringify(value) : asString(value);
    if (text.length === 0) continue;
    if (isCard) {
      if (key === 'cardNumber') typed['number'] = text;
      else if (key === 'nameOnCard') typed['cardholder'] = text;
      else if (key === 'security_code') typed['cvv'] = text;
      else if (key === 'expires') {
        const expiry = cardExpiry(text);
        if (expiry !== null) typed['expiry'] = expiry;
        else customFields.push({ name: 'Expiration', value: text, hidden: false });
      } else customFields.push(csvColumnField(key, text));
      continue;
    }
    if ((key === 'username' || key === 'email') && username.length === 0) username = text;
    else if (key === 'password') password = rawSecret(value);
    else if (key === 'totp') totp = normalizeTotpSecret(text);
    else if (URL_KEYS.has(key) && url.length === 0) url = text;
    else customFields.push(csvColumnField(key, text));
  }

  const folderName = folders.get(asString(item['folder']));
  const tags = asArray(item['tags']).map(asString).filter(Boolean);
  const base = {
    username: '',
    password: '',
    uris: [] as string[],
    notes: joinNotes([passwordBossNotes(identifiers['notes']), totp.extraNote]),
    totpSecret: totp.secret,
    favorite: false,
    ...(folderName !== undefined && folderName.length > 0 ? { folderPath: [folderName] } : {}),
    ...(tags.length > 0 ? { tags } : {}),
  };
  const name = asString(item['name']);
  if (isCard) {
    return { ...base, type: 'card', name: name || 'Carte', fields: normalizeTypedFields('card', typed), ...withFields(customFields) };
  }
  // Ni identifiant, ni mot de passe, ni adresse, ni code : c'est une note securisee.
  if (username.length === 0 && password.length === 0 && url.length === 0 && totp.secret.length === 0) {
    return { ...base, type: 'note', name: name || 'Sans nom', ...withFields(customFields) };
  }
  return {
    ...base,
    type: 'login',
    name: name || (url.length > 0 ? hostOf(url) : '') || username || 'Sans nom',
    username,
    password,
    uris: url.length > 0 ? [url] : [],
    ...withFields(customFields),
  };
}

/**
 * Un export Password Boss se reconnait a un element de `items` dont les
 * valeurs sont rangees dans un objet `identifiers` : un tableau `items` seul
 * ne suffit pas (Bitwarden, Enpass et Psono en portent un).
 */
export function isPasswordBossExport(data: Json): boolean {
  const file = asObject(data);
  return file !== null && objects(file['items']).some((item) => asObject(item['identifiers']) !== null);
}

/** Parse un export Password Boss JSON : chaque element, rattache a son dossier. */
export function parsePasswordBossExport(data: Json): ImportedEntry[] {
  const file = asObject(data);
  if (file === null) throw new Error('export Password Boss : structure inattendue');
  const folders = new Map<string, string>();
  for (const folder of objects(file['folders'])) {
    const id = asString(folder['id']);
    if (id.length > 0) folders.set(id, asString(folder['name']));
  }
  return objects(file['items']).map((item) => parsePasswordBossItem(item, folders));
}
